// wayai use [--add] <selector> — scope this worktree to a hub, a base or a community.
//
// Without `--add` it REPLACES that axis (the single-binding rebind semantics
// this command has always had). With `--add` it appends, which is the sanctioned
// widen the guard's own refusal points at: a task legitimately spanning two hubs,
// or a base serving several hubs, widens the scope rather than clearing it.

import { expected } from '../lib/expected.js';
import {
  findAddFlag,
  isValuedFlag,
  resolveScopeSelector,
  resolveScopeTarget,
} from '../lib/scope-selector.js';
import { addToScope, axisNoun, describeAxis, readScope, setScopeAxis } from '../lib/worktree-scope.js';

const USAGE = [
  'Usage: wayai use [--add] <hub_uuid|folder-name|hubs/<name>|bases/<name>|communities/<name>>',
  '       wayai use [--add] --hub <hub> | --base <base> | --community <community>',
  '',
  '  --add   widen the worktree scope instead of replacing that axis',
].join('\n');

// Every failure below is raised as an `expected` error rather than caught here.
// `runForeground` in `index.ts` already prints it to stderr, masks secrets, adds
// the friendly hint, and exits 1 — and keeps it out of Sentry precisely because
// it is tagged expected. A local catch-all would print the message a second way
// while ALSO routing a genuine crash (a malformed workspace throwing inside the
// resolver) down that same silent path, and that is the one error that SHOULD
// reach Sentry. The scope guards themselves still print-and-exit: they fire
// mid-command as a deliberate routing refusal, the same posture as
// `requireSubtree`.
export async function useCommand(args: string[]): Promise<void> {
  const addFlag = findAddFlag(args);
  if (addFlag !== undefined && isValuedFlag(addFlag)) {
    throw expected(
      [
        `\`--add\` is a boolean flag — write it bare, not ${JSON.stringify(addFlag)}.`,
        '  wayai use --add <selector>   # widen the worktree scope',
        '  wayai use <selector>         # replace that axis instead',
      ].join('\n'),
    );
  }
  const add = addFlag !== undefined;

  const selector = resolveScopeSelector('use', args);
  if (!selector) throw expected(USAGE);
  const { axis, id } = resolveScopeTarget(selector);
  const noun = axisNoun(axis);

  if (add) {
    const widened = addToScope(axis, id);
    console.log(
      widened
        ? `Worktree scope widened — now ${describeAxis(axis, widened)}.`
        : `Worktree scope already includes ${noun} ${id}.`,
    );
    return;
  }

  const previous = readScope()[axis];
  if (previous.length === 1 && previous[0] === id) {
    console.log(`Worktree already bound to ${noun} ${id}.`);
    return;
  }
  setScopeAxis(axis, [id]);
  console.log(
    previous.length > 0
      ? `Worktree rebound: ${previous.join(', ')} → ${id}.`
      : `Worktree bound to ${noun} ${id}.`,
  );
}
